import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import {
  makeTenantPartySchema,
  type TenantPartyFormValues,
} from '@/schemas';
import { FormTextField } from '@/components';
import {
  createTenantParty,
  getTenantParty,
  updateTenantParty,
} from '@/api/tenantParties';
import { useAuthStore } from '@/stores/authStore';
import { brand } from '@/theme/theme';

/**
 * Create / Edit form for a tenant party (a row in the "My Parties" tab).
 *
 * Routes:
 *   /tenant-parties/new            — create
 *   /tenant-parties/:id/edit       — edit
 *
 * Layout mirrors the legacy "Create Party" screen: a two-column grid
 * with identity + address on the left and contact + tax/licence on the
 * right, Cancel + Submit at the bottom.
 *
 * Validation is driven by {@link makeTenantPartySchema}, which is the
 * standard {@link tenantPartySchema} extended with an industry-aware
 * rule: when the signed-in tenant's industry is PHARMACEUTICAL, the DL
 * Number field is required. Other industries leave it optional.
 */

const PARTY_TYPE_OPTIONS: Array<{
  value: NonNullable<TenantPartyFormValues['partyType']>;
  label: string;
}> = [
  { value: 'RETAILER', label: 'Retailer' },
  { value: 'WHOLESALER', label: 'Wholesaler' },
  { value: 'DISTRIBUTOR', label: 'Distributor' },
  { value: 'OTHERS', label: 'Others' },
];

const EMPTY_FORM: TenantPartyFormValues = {
  name: '',
  partyCode: '',
  partyExternalId: '',
  partyType: undefined,
  contactPerson: '',
  mobile: '',
  phone: '',
  email: '',
  gstin: '',
  panNumber: '',
  dlNumber: '',
  dlExpiry: '',
  repName: '',
  addressLine1: '',
  addressLine2: '',
  area: '',
  city: '',
  pincode: '',
};

export function TenantPartyFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const numericId = id ? Number(id) : null;

  // Used to gate the "DL Number is required" rule. Pulled from the auth
  // store so the form is industry-aware without an extra API call.
  const tenantIndustry = useAuthStore((s) => s.user?.industry);
  const isPharma = tenantIndustry === 'PHARMACEUTICAL';

  // Memoise the schema so we don't rebuild it on every render. The rule
  // only changes when the tenant logs out and a new tenant logs in,
  // which remounts the page anyway.
  const schema = useMemo(
    () => makeTenantPartySchema(tenantIndustry),
    [tenantIndustry],
  );

  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<TenantPartyFormValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_FORM,
  });

  // ---- Edit-mode prefill ----
  useEffect(() => {
    if (!isEdit || numericId == null) return;
    let cancelled = false;
    (async () => {
      try {
        const tp = await getTenantParty(numericId);
        if (cancelled) return;
        reset({
          name: tp.name ?? '',
          partyCode: tp.partyCode ?? '',
          partyExternalId: tp.partyExternalId ?? '',
          partyType: tp.partyType,
          contactPerson: tp.contactPerson ?? '',
          mobile: tp.mobile ?? '',
          phone: tp.phone ?? '',
          email: tp.email ?? '',
          gstin: tp.gstin ?? '',
          panNumber: tp.panNumber ?? '',
          dlNumber: tp.dlNumber ?? '',
          dlExpiry: tp.dlExpiry ?? '',
          repName: tp.repName ?? '',
          addressLine1: tp.addressLine1 ?? '',
          addressLine2: tp.addressLine2 ?? '',
          area: tp.area ?? '',
          city: tp.city ?? '',
          pincode: tp.pincode ?? '',
        });
      } catch {
        if (!cancelled) {
          setServerError('Could not load this party. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, numericId, reset]);

  // ---- Submit ----
  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    setSubmitting(true);
    try {
      // Strip empty strings to undefined — the backend treats them as null
      // and the @Email validator on the DTO rejects "" as invalid.
      const payload: TenantPartyFormValues = Object.fromEntries(
        Object.entries(values).map(([k, v]) => [k, v === '' ? undefined : v]),
      ) as TenantPartyFormValues;

      if (isEdit && numericId != null) {
        await updateTenantParty(numericId, payload);
      } else {
        await createTenantParty(payload);
      }
      navigate('/network?tab=my', { state: { created: !isEdit, updated: isEdit } });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Could not save the party. Please try again.';
      setServerError(msg);
    } finally {
      setSubmitting(false);
    }
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/network?tab=my')}
            sx={{ color: brand.inkSoft, textTransform: 'none' }}
          >
            Back
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 700, color: brand.navy }}>
            {isEdit ? 'Edit Party' : 'Create Party'}
          </Typography>
        </Stack>
      </Stack>

      <Card variant="outlined">
        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          {serverError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {serverError}
            </Alert>
          )}

          <Box component="form" onSubmit={onSubmit} noValidate>
            <Grid container spacing={3}>
              {/* ---------- LEFT COLUMN ---------- */}
              <Grid item xs={12} md={6}>
                <Stack spacing={2.25}>
                  <FormTextField<TenantPartyFormValues>
                    name="name"
                    register={register}
                    errors={errors}
                    label="Party Name"
                    placeholder="Party Name"
                    required
                  />

                  <FormTextField<TenantPartyFormValues>
                    name="partyCode"
                    register={register}
                    errors={errors}
                    label="Party Code"
                    placeholder="Code"
                  />

                  <FormTextField<TenantPartyFormValues>
                    name="partyExternalId"
                    register={register}
                    errors={errors}
                    label="Party ID"
                    placeholder="Party ID"
                  />

                  <Controller
                    name="partyType"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        label="Party Type"
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(e.target.value || undefined)
                        }
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {PARTY_TYPE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />

                  <FormTextField<TenantPartyFormValues>
                    name="addressLine1"
                    register={register}
                    errors={errors}
                    label="Address Line 1"
                    placeholder="Address Line 1"
                  />

                  <FormTextField<TenantPartyFormValues>
                    name="addressLine2"
                    register={register}
                    errors={errors}
                    label="Address Line 2"
                    placeholder="Address Line 1"
                  />

                  <FormTextField<TenantPartyFormValues>
                    name="area"
                    register={register}
                    errors={errors}
                    label="Area"
                    placeholder="Area"
                  />

                  <FormTextField<TenantPartyFormValues>
                    name="city"
                    register={register}
                    errors={errors}
                    label="City"
                    placeholder="City"
                  />

                  <FormTextField<TenantPartyFormValues>
                    name="pincode"
                    register={register}
                    errors={errors}
                    label="Pincode"
                    placeholder="Pincode"
                  />
                </Stack>
              </Grid>

              {/* ---------- RIGHT COLUMN ---------- */}
              <Grid item xs={12} md={6}>
                <Stack spacing={2.25}>
                  <FormTextField<TenantPartyFormValues>
                    name="contactPerson"
                    register={register}
                    errors={errors}
                    label="Contact Person"
                    placeholder="Contact Person"
                  />

                  <FormTextField<TenantPartyFormValues>
                    name="mobile"
                    register={register}
                    errors={errors}
                    label="Mobile"
                    placeholder="Mobile"
                  />

                  <FormTextField<TenantPartyFormValues>
                    name="phone"
                    register={register}
                    errors={errors}
                    label="Phone"
                    placeholder="Phone"
                  />

                  <FormTextField<TenantPartyFormValues>
                    name="email"
                    register={register}
                    errors={errors}
                    label="Email"
                    placeholder="Email"
                    type="email"
                  />

                  <FormTextField<TenantPartyFormValues>
                    name="gstin"
                    register={register}
                    errors={errors}
                    label="GSTIN"
                    placeholder="GSTIN"
                  />

                  <FormTextField<TenantPartyFormValues>
                    name="panNumber"
                    register={register}
                    errors={errors}
                    label="PAN Number"
                    placeholder="PAN Number"
                  />

                  <FormTextField<TenantPartyFormValues>
                    name="dlNumber"
                    register={register}
                    errors={errors}
                    label="DL Number"
                    placeholder="DL Number"
                    required={isPharma}
                    helperText={
                      isPharma
                        ? 'Required for pharmaceutical tenants'
                        : undefined
                    }
                  />

                  <FormTextField<TenantPartyFormValues>
                    name="dlExpiry"
                    register={register}
                    errors={errors}
                    label="DL Expiry"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />

                  <FormTextField<TenantPartyFormValues>
                    name="repName"
                    register={register}
                    errors={errors}
                    label="Rep Name"
                    placeholder="Rep Name"
                  />
                </Stack>
              </Grid>
            </Grid>

            {/* ---------- ACTIONS ---------- */}
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              sx={{ mt: 4 }}
            >
              <Button
                variant="outlined"
                onClick={() => navigate('/network?tab=my')}
                disabled={submitting}
                sx={{ minWidth: 140 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={submitting}
                sx={{ minWidth: 140 }}
              >
                {submitting ? 'Saving…' : 'Submit'}
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
